import { useCallback, useEffect, useState } from "react";

import { type BasemapId, isBasemapId } from "../map/map-style.ts";

const STORAGE_KEY = "boomap.basemap";

/**
 * Le fond clair par défaut : la carte se consulte surtout de jour, pour préparer
 * la tournée, et l'habillage clair est celui qui s'affiche avant tout script.
 * Le mode nuit reste à un clic.
 */
const DEFAULT_BASEMAP: BasemapId = "day";

/**
 * Le fond de carte est une préférence d'affichage, purement locale : elle vit
 * dans `localStorage`, ne part jamais au serveur, et l'application doit
 * fonctionner si le stockage est refusé (navigation privée, site data bloqué).
 */
const readStored = (): BasemapId => {
  try {
    const stored = globalThis.localStorage?.getItem(STORAGE_KEY);

    return isBasemapId(stored) ? stored : DEFAULT_BASEMAP;
  } catch {
    return DEFAULT_BASEMAP;
  }
};

export interface UseBasemapResult {
  readonly basemap: BasemapId;
  readonly setBasemap: (basemap: BasemapId) => void;
}

export const useBasemap = (): UseBasemapResult => {
  const [basemap, setState] = useState<BasemapId>(readStored);

  const setBasemap = useCallback((next: BasemapId) => {
    setState(next);

    try {
      globalThis.localStorage?.setItem(STORAGE_KEY, next);
    } catch {
      // Préférence non mémorisable : sans conséquence pour la session en cours.
    }
  }, []);

  /*
   * L'habillage suit le fond de carte : des panneaux sombres sur une carte
   * claire se lisaient mal. Posé sur la racine du document et non sur un nœud
   * de l'application, pour atteindre aussi le dialogue rendu dans `body`.
   */
  useEffect(() => {
    document.documentElement.dataset["theme"] = basemap === "day" ? "day" : "night";
  }, [basemap]);

  return { basemap, setBasemap };
};
